import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, FileUp, Send, Building2, User, MessageSquare, CheckCircle2 } from "lucide-react";
import { COMPANY } from "@/lib/company";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({
    full_name: "",
    company_name: "",
    sourcing_needs: "",
    email: "",
    phone: "",
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [mailtoLink, setMailtoLink] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.company_name || !form.sourcing_needs) {
      toast.error("Please complete required fields: Name, Company, and Sourcing Needs.");
      return;
    }
    setSubmitting(true);
    try {
      const data = new FormData();
      data.append("full_name", form.full_name);
      data.append("company_name", form.company_name);
      data.append("sourcing_needs", form.sourcing_needs);
      if (form.email) data.append("email", form.email);
      if (form.phone) data.append("phone", form.phone);
      if (file) data.append("invoice", file);

      const res = await axios.post(`${API}/contact/submit`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const subject = encodeURIComponent(`New Sourcing Request — ${form.company_name}`);
      const lines = [
        `New B2B Sourcing Request submitted via allinsource.com`,
        ``,
        `Full Name: ${form.full_name}`,
        `Company:   ${form.company_name}`,
        `Email:     ${form.email || "(not provided)"}`,
        `Phone:     ${form.phone || "(not provided)"}`,
        ``,
        `Current Sourcing Needs:`,
        form.sourcing_needs,
        ``,
        `Invoice Attached: ${file ? file.name + " (uploaded to A.I.O portal)" : "None"}`,
        ``,
        `Submission ID: ${res.data?.submission_id || "n/a"}`,
        `— Sent from the All In One Sourcing Specialists A.I.O inquiry form`,
      ];
      const body = encodeURIComponent(lines.join("\n"));
      const mailto = `${COMPANY.emailLink}?subject=${subject}&body=${body}`;
      setMailtoLink(mailto);

      toast.success(res.data.message || "Sourcing request submitted.");
      setSubmitted(true);
      setForm({ full_name: "", company_name: "", sourcing_needs: "", email: "", phone: "" });
      setFile(null);

      // Best-effort auto-open of mail client (may be popup-blocked)
      try { window.open(mailto, "_blank"); } catch (_) { /* swallow */ }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#020617]">
      {/* HERO */}
      <section className="relative overflow-hidden aios-starfield">
        <div className="absolute inset-0 aios-grid-overlay opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/40 via-transparent to-[#020617]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-28">
          <div className="max-w-4xl">
            <span className="text-red-500 font-display text-xs tracking-[0.3em] font-bold">
              THE RISK-FREE INQUIRY
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-white mt-4">
              Still Skeptical? <br />
              <span className="text-red-500">No Pressure.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl">
              Send us your sourcing brief, attach your current invoice if you have one, and we'll show you exactly where the savings hide.
              No portal activation required. No card on file. Just a straight, aggressive sourcing quote.
            </p>
          </div>
        </div>
      </section>

      {/* MAIN GRID */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-5 gap-12">
          {/* FORM */}
          <div className="lg:col-span-3">
            <div className="aios-card p-8 lg:p-10" data-testid="contact-form-card">
              <div className="aios-card-accent" />
              <h2 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight">
                Submit a Sourcing Request
              </h2>
              <p className="text-slate-400 mt-2 text-sm">
                Our specialists respond within <span className="text-red-400 font-semibold">24 business hours</span>.
              </p>

              {submitted ? (
                <div
                  data-testid="contact-success-state"
                  className="mt-10 border-l-2 border-red-600 pl-6 py-4"
                >
                  <div className="flex items-center gap-2 text-red-500">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-display tracking-widest uppercase text-xs font-bold">
                      Request Received
                    </span>
                  </div>
                  <p className="text-white text-lg mt-3 font-display font-semibold">
                    Thank you. A sourcing specialist will contact you within 24 business hours.
                  </p>
                  <p className="text-slate-400 text-sm mt-3 leading-relaxed">
                    We tried to auto-open your email client with a copy of the inquiry addressed to <span className="text-red-400">{COMPANY.email}</span>.
                    If your browser blocked it, use the button below.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    {mailtoLink && (
                      <a
                        href={mailtoLink}
                        data-testid="contact-email-copy-fallback"
                        className="aios-btn-primary text-xs"
                      >
                        <Mail className="w-4 h-4" />
                        Email a Copy of My Inquiry
                      </a>
                    )}
                    <button
                      onClick={() => { setSubmitted(false); setMailtoLink(""); }}
                      data-testid="contact-submit-another"
                      className="aios-btn-secondary text-xs"
                    >
                      Submit Another Request
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="mt-8 space-y-6" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field
                      icon={User}
                      label="Full Name *"
                      name="full_name"
                      value={form.full_name}
                      onChange={handleChange}
                      testId="contact-input-name"
                      required
                    />
                    <Field
                      icon={Building2}
                      label="Company Name *"
                      name="company_name"
                      value={form.company_name}
                      onChange={handleChange}
                      testId="contact-input-company"
                      required
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field
                      icon={Mail}
                      label="Email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      testId="contact-input-email"
                    />
                    <Field
                      icon={Phone}
                      label="Phone"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      testId="contact-input-phone"
                    />
                  </div>

                  <div>
                    <label className="font-display text-xs tracking-widest font-bold text-slate-300 uppercase mb-2 flex items-center gap-2">
                      <MessageSquare className="w-3.5 h-3.5 text-red-500" />
                      Current Sourcing Needs *
                    </label>
                    <textarea
                      name="sourcing_needs"
                      value={form.sourcing_needs}
                      onChange={handleChange}
                      required
                      rows={5}
                      data-testid="contact-input-needs"
                      placeholder="Tell us what your business is sourcing — real estate, equipment, wholesale, services, turnkey…"
                      className="w-full bg-[#0B1221] border border-slate-700 focus:border-red-600 focus:outline-none px-4 py-3 text-slate-100 placeholder:text-slate-600 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="font-display text-xs tracking-widest font-bold text-slate-300 uppercase mb-2 flex items-center gap-2">
                      <FileUp className="w-3.5 h-3.5 text-red-500" />
                      Invoice Upload (optional)
                    </label>
                    <input
                      type="file"
                      data-testid="contact-input-invoice"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv"
                      className="w-full text-sm text-slate-400 file:mr-4 file:py-3 file:px-6 file:border-0 file:bg-red-600 file:text-white file:font-display file:font-bold file:uppercase file:tracking-widest file:text-xs file:cursor-pointer hover:file:bg-red-700"
                    />
                    {file && (
                      <p className="mt-2 text-xs text-slate-400" data-testid="contact-file-name">
                        Attached: {file.name}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-form-submit"
                    className="aios-btn-primary w-full text-sm"
                  >
                    <Send className="w-4 h-4" />
                    {submitting ? "Submitting..." : "Submit Sourcing Request"}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* ACTION BUTTONS */}
          <aside className="lg:col-span-2 space-y-5">
            <div className="aios-card p-8" data-testid="contact-direct-actions">
              <div className="aios-card-accent" />
              <span className="text-red-500 font-display text-xs tracking-[0.3em] font-bold">
                IMMEDIATE CONTACT
              </span>
              <h3 className="font-display text-2xl font-bold text-white tracking-tight mt-3">
                Prefer to talk to a human right now?
              </h3>
              <p className="text-slate-400 text-sm mt-3 leading-relaxed">
                Pick up the phone or hit us by email — we route all inbound directly to a senior sourcing specialist.
              </p>

              <a
                href={COMPANY.phoneLink}
                data-testid="contact-call-btn"
                className="aios-btn-primary mt-6 w-full text-sm"
              >
                <Phone className="w-4 h-4" />
                📞 [ CALL US NOW ]
              </a>
              <div className="mt-3 text-center text-slate-300 font-display font-bold tracking-wider">
                {COMPANY.phone}
              </div>

              <a
                href={COMPANY.emailLink}
                data-testid="contact-email-btn"
                className="aios-btn-secondary mt-5 w-full text-sm"
              >
                <Mail className="w-4 h-4" />
                ✉️ [ EMAIL US FOR A SOURCING QUOTE ]
              </a>
              <div className="mt-3 text-center text-slate-400 text-sm break-all">
                {COMPANY.email}
              </div>
            </div>

            <div className="aios-card p-8">
              <div className="aios-card-accent" />
              <h4 className="font-display font-bold text-white text-lg tracking-tight">
                What happens after you submit?
              </h4>
              <ol className="mt-4 space-y-3 text-sm text-slate-400">
                {[
                  "A senior sourcing specialist reads your request — no chatbots, no autoresponders.",
                  "We benchmark your current spend against our portal pricing.",
                  "You get a no-pressure sourcing quote with hard numbers.",
                  "If the math works for you, we activate your portal. If not, we shake hands.",
                ].map((step, idx) => (
                  <li key={idx} className="flex gap-3">
                    <span className="font-display font-extrabold text-red-500 w-5 shrink-0">0{idx + 1}</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Field({ icon: Icon, label, testId, ...rest }) {
  return (
    <div>
      <label className="font-display text-xs tracking-widest font-bold text-slate-300 uppercase mb-2 flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-red-500" />}
        {label}
      </label>
      <input
        {...rest}
        data-testid={testId}
        className="w-full bg-[#0B1221] border border-slate-700 focus:border-red-600 focus:outline-none px-4 py-3 text-slate-100 placeholder:text-slate-600 transition-colors"
      />
    </div>
  );
}
